import { useCallback } from 'react';
import { string } from 'yup';

import { type AirActionTrackingLocation, useTrackCreatedBoard } from '@air/analytics';
import { Boards } from '@air/api';
import { isAirror } from '@air/errors';
import { useToasts } from '@air/provider-toast';
import { reportErrorToBugsnag } from '@air/utils-error';

import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { useFollowBoard } from '~/swr-hooks/followedBoards/useFollowBoard';
import { containsRestrictedPathChars, sanitizeRestrictedPathChars } from '~/utils/FileUtils';
import { useAddBoardsToAllLists } from '~/utils/mutateUtils/AllBoards';
import { WORKSPACE_NAME_MAX_LENGTH } from '~/utils/validationSchemas';

export const BoardTitleValidation = string()
  .trim()
  .max(WORKSPACE_NAME_MAX_LENGTH, `Cannot be longer than ${WORKSPACE_NAME_MAX_LENGTH} characters`)
  .test(
    'restricted-char-validation',
    'Characters : and | cannot be used',
    (val) => !containsRestrictedPathChars(val || ''),
  )
  .matches(/^[^\n]*$/, 'No line breaks allowed')
  .required('Name is required');

export type CreateNewBoardParams = Pick<
  Extract<Parameters<typeof Boards.create>[0], { board: unknown }>,
  'board' | 'clipIds' | 'shouldFollow'
> & {
  trackLocation: AirActionTrackingLocation;
};

export const useCreateNewBoard = () => {
  const { showToast } = useToasts();
  const {
    followBoard: { mutate: followBoardMutate },
  } = useFollowBoard();
  const { addBoardsToAllLists } = useAddBoardsToAllLists();
  const { trackCreatedBoard } = useTrackCreatedBoard();
  const { currentWorkspace } = useCurrentWorkspace();
  const workspaceId = currentWorkspace?.id;

  const createNewBoard = useCallback(
    async ({ board, clipIds, trackLocation, shouldFollow }: CreateNewBoardParams) => {
      if (!workspaceId) {
        throw new Error('No workspaceId found');
      }

      try {
        const { board: boardResponse } = await Boards.create({
          workspaceId,
          board: {
            ...board,
            title: sanitizeRestrictedPathChars(board.title.trim()),
            parentId: board.parentId || undefined,
          },
          clipIds,
        });

        if (shouldFollow) {
          await followBoardMutate({ boardId: boardResponse.id, includeDescendants: false });
        }

        addBoardsToAllLists([boardResponse], board.parentId);

        trackCreatedBoard({
          ...boardResponse,
          clipIds,
          location: trackLocation,
        });

        return boardResponse;
      } catch (error) {
        showToast(isAirror(error) ? error.message : 'Failed to create board due to an unknown error', { color: 'red' });
        if (!isAirror(error)) {
          throw reportErrorToBugsnag({
            error,
            context: 'Failed to create board',
            metadata: { Data: { board, clipIds, shouldFollow, trackLocation } },
          });
        }
      }
    },
    [workspaceId, addBoardsToAllLists, trackCreatedBoard, followBoardMutate, showToast],
  );

  return {
    createNewBoard,
  };
};
